/**
 * usePolledOrderbook Hook
 *
 * Polls orderbook data via CCXT REST API every 1 second until orderbook has
 * sufficient depth (at least MIN_DEPTH levels on each side).
 *
 * Uses CCXT's fetchOrderBook method for direct exchange access.
 * For Upbit, uses direct API call to avoid 429 rate limit on loadMarkets().
 */

import { useState, useEffect, useRef } from 'react';
import type { OrderbookPriceLevel } from '../types/orderbook';
import { getApiConfigCached } from '../config/api.config';
import { fetchFuturesOrderbook } from '../services/orderbookService';

/**
 * Polling interval in milliseconds
 */
const POLL_INTERVAL_MS = 1000;

/**
 * Upbit orderbook API response types
 */
interface UpbitOrderbookUnit {
  ask_price: number;
  bid_price: number;
  ask_size: number;
  bid_size: number;
}

interface UpbitOrderbookResponse {
  market: string;
  timestamp: number;
  total_ask_size: number;
  total_bid_size: number;
  orderbook_units: UpbitOrderbookUnit[];
}

/**
 * Fetch Upbit orderbook from our internal API (in-memory cache)
 * Used as fallback when direct Upbit API fails
 *
 * @param symbol - CCXT format symbol (e.g., "BTC/KRW")
 * @returns Orderbook data or throws error
 */
async function fetchUpbitOrderbookFromServer(symbol: string): Promise<{ bids: OrderbookPriceLevel[]; asks: OrderbookPriceLevel[] }> {
  const [base, quote] = symbol.split('/');
  const apiConfig = getApiConfigCached();

  // Build URL: /api/orderbook/upbit/{base}{quote}
  let url: string;
  if (apiConfig.isProduction) {
    url = `${window.location.protocol}//${apiConfig.host}/api/orderbook/upbit/${base}${quote}`;
  } else {
    const port = apiConfig.wsPort || 8765;
    url = `http://${apiConfig.host}:${port}/api/orderbook/upbit/${base}${quote}`;
  }

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Server API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Server returns { bids: [{ price, volume }, ...], asks: [{ price, volume }, ...] }
  const bids: OrderbookPriceLevel[] = (data.bids || []).map((level: { price: number; volume: number }) => ({
    price: level.price,
    volume: level.volume,
  }));

  const asks: OrderbookPriceLevel[] = (data.asks || []).map((level: { price: number; volume: number }) => ({
    price: level.price,
    volume: level.volume,
  }));

  return { bids, asks };
}

/**
 * Bithumb orderbook API response types
 */
interface BithumbOrderbookResponse {
  status: string;
  data: {
    timestamp: string;
    payment_currency: string;
    order_currency: string;
    bids: Array<{ price: string; quantity: string }>;
    asks: Array<{ price: string; quantity: string }>;
  };
}

/**
 * Fetch Bithumb orderbook from our internal API (in-memory cache)
 * Used as fallback when direct Bithumb API fails
 *
 * @param symbol - CCXT format symbol (e.g., "BTC/KRW")
 * @returns Orderbook data or throws error
 */
async function fetchBithumbOrderbookFromServer(symbol: string): Promise<{ bids: OrderbookPriceLevel[]; asks: OrderbookPriceLevel[] }> {
  const [base, quote] = symbol.split('/');
  const apiConfig = getApiConfigCached();

  // Build URL: /api/orderbook/bithumb/{base}{quote}
  let url: string;
  if (apiConfig.isProduction) {
    url = `${window.location.protocol}//${apiConfig.host}/api/orderbook/bithumb/${base}${quote}`;
  } else {
    const port = apiConfig.wsPort || 8765;
    url = `http://${apiConfig.host}:${port}/api/orderbook/bithumb/${base}${quote}`;
  }

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Server API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Server returns { bids: [{ price, volume }, ...], asks: [{ price, volume }, ...] }
  const bids: OrderbookPriceLevel[] = (data.bids || []).map((level: { price: number; volume: number }) => ({
    price: level.price,
    volume: level.volume,
  }));

  const asks: OrderbookPriceLevel[] = (data.asks || []).map((level: { price: number; volume: number }) => ({
    price: level.price,
    volume: level.volume,
  }));

  return { bids, asks };
}

/**
 * Fetch Coinone orderbook from our internal API (in-memory cache)
 *
 * @param symbol - CCXT format symbol (e.g., "BTC/KRW")
 * @returns Orderbook data or throws error
 */
async function fetchCoinoneOrderbookFromServer(symbol: string): Promise<{ bids: OrderbookPriceLevel[]; asks: OrderbookPriceLevel[] }> {
  const [base, quote] = symbol.split('/');
  const apiConfig = getApiConfigCached();

  // Build URL: /api/orderbook/coinone/{base}{quote}
  let url: string;
  if (apiConfig.isProduction) {
    url = `${window.location.protocol}//${apiConfig.host}/api/orderbook/coinone/${base}${quote}`;
  } else {
    const port = apiConfig.wsPort || 8765;
    url = `http://${apiConfig.host}:${port}/api/orderbook/coinone/${base}${quote}`;
  }

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Server API error: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // Server returns { bids: [{ price, volume }, ...], asks: [{ price, volume }, ...] }
  const bids: OrderbookPriceLevel[] = (data.bids || []).map((level: { price: number; volume: number }) => ({
    price: level.price,
    volume: level.volume,
  }));

  const asks: OrderbookPriceLevel[] = (data.asks || []).map((level: { price: number; volume: number }) => ({
    price: level.price,
    volume: level.volume,
  }));

  return { bids, asks };
}

/**
 * Fetch Coinone orderbook - uses internal server API
 * (Coinone's public API has CORS restrictions)
 *
 * @param symbol - CCXT format symbol (e.g., "BTC/KRW")
 * @returns Orderbook data or throws error
 */
async function fetchCoinoneOrderbook(symbol: string): Promise<{ bids: OrderbookPriceLevel[]; asks: OrderbookPriceLevel[] }> {
  // Coinone public API has CORS issues, use server directly
  return fetchCoinoneOrderbookFromServer(symbol);
}

/**
 * Fetch Bithumb orderbook directly via REST API
 * Falls back to internal server API if direct call fails (CORS, rate limit, etc.)
 *
 * Note: Uses direct API because CCXT's Bithumb market list is often outdated
 * and missing newer tokens (e.g., ALT/KRW).
 *
 * @param symbol - CCXT format symbol (e.g., "BTC/KRW")
 * @returns Orderbook data or throws error
 */
async function fetchBithumbOrderbook(symbol: string): Promise<{ bids: OrderbookPriceLevel[]; asks: OrderbookPriceLevel[] }> {
  // Convert CCXT symbol (BTC/KRW) to Bithumb native format (BTC_KRW)
  const [base, quote] = symbol.split('/');
  const bithumbMarket = `${base}_${quote}`;

  try {
    const url = `https://api.bithumb.com/public/orderbook/${bithumbMarket}`;

    const response = await fetch(url, {
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Bithumb API error: ${response.status} ${response.statusText}`);
    }

    const data: BithumbOrderbookResponse = await response.json();

    if (data.status !== '0000') {
      throw new Error(`Bithumb API status error: ${data.status}`);
    }

    // Convert Bithumb format to our format
    // Bithumb returns separate bids and asks arrays with string values
    const bids: OrderbookPriceLevel[] = data.data.bids.map((level) => ({
      price: parseFloat(level.price),
      volume: parseFloat(level.quantity),
    }));

    const asks: OrderbookPriceLevel[] = data.data.asks.map((level) => ({
      price: parseFloat(level.price),
      volume: parseFloat(level.quantity),
    }));

    return { bids, asks };
  } catch (directError) {
    // Fallback to internal server API
    console.warn(`[usePolledOrderbook] Direct Bithumb API failed (${directError}), using server fallback`);
    return fetchBithumbOrderbookFromServer(symbol);
  }
}

/**
 * Fetch Upbit orderbook directly via REST API
 * Falls back to internal server API if direct call fails (CORS, rate limit, etc.)
 *
 * @param symbol - CCXT format symbol (e.g., "BTC/KRW")
 * @returns Orderbook data or throws error
 */
async function fetchUpbitOrderbook(symbol: string): Promise<{ bids: OrderbookPriceLevel[]; asks: OrderbookPriceLevel[] }> {
  // Convert CCXT symbol (BTC/KRW) to Upbit native format (KRW-BTC)
  const [base, quote] = symbol.split('/');
  const upbitMarket = `${quote}-${base}`;

  try {
    const url = `https://api.upbit.com/v1/orderbook?markets=${upbitMarket}`;

    const response = await fetch(url, {
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Upbit API error: ${response.status} ${response.statusText}`);
    }

    const data: UpbitOrderbookResponse[] = await response.json();

    if (!data || data.length === 0) {
      throw new Error(`No orderbook data for ${upbitMarket}`);
    }

    const orderbook = data[0];

    // Convert Upbit format to our format
    // Upbit returns orderbook_units with combined bid/ask at each level
    const bids: OrderbookPriceLevel[] = orderbook.orderbook_units.map((unit) => ({
      price: unit.bid_price,
      volume: unit.bid_size,
    }));

    const asks: OrderbookPriceLevel[] = orderbook.orderbook_units.map((unit) => ({
      price: unit.ask_price,
      volume: unit.ask_size,
    }));

    return { bids, asks };
  } catch (directError) {
    // Fallback to internal server API
    console.warn(`[usePolledOrderbook] Direct Upbit API failed (${directError}), using server fallback`);
    return fetchUpbitOrderbookFromServer(symbol);
  }
}

// Dynamic CCXT import (browser-compatible)
type CcxtModule = typeof import('ccxt');
type CcxtExchangeType = import('ccxt').Exchange;

let ccxtModulePromise: Promise<CcxtModule> | null = null;

// Cache exchange instances to avoid re-initialization overhead
const exchangeCache: Map<string, CcxtExchangeType> = new Map();

async function loadCcxtModule(): Promise<CcxtModule> {
  if (!ccxtModulePromise) {
    ccxtModulePromise = import('ccxt').then((raw) => {
      return (raw.default ?? raw) as CcxtModule;
    }).catch((error) => {
      console.error('[usePolledOrderbook] CCXT import FAILED:', error);
      throw error;
    });
  }
  return ccxtModulePromise;
}

/**
 * Preload CCXT module and common exchange markets in the background.
 * CCXT automatically calls loadMarkets() on first API call, which is slow.
 * By preloading here, the dialog opens instantly.
 */
export async function preloadCcxt(): Promise<void> {
  if (ccxtModulePromise && exchangeCache.size > 0) {
    // Already preloaded
    return;
  }

  console.log('[usePolledOrderbook] Preloading CCXT module and exchanges...');

  try {
    const ccxtModule = await loadCcxtModule();

    // Preload common exchanges in parallel - this triggers loadMarkets() for each
    // Note: Upbit is excluded because we use direct API to avoid 429 rate limit
    const exchangesToPreload = [
      { name: 'binanceusdm', isFutures: true },
      { name: 'bithumb', isFutures: false },
    ];

    await Promise.all(
      exchangesToPreload.map(async ({ name, isFutures }) => {
        try {
          const exchange = await getOrCreateExchange(ccxtModule, name, isFutures);
          // Force loadMarkets() by calling it directly
          if (!exchange.markets || Object.keys(exchange.markets).length === 0) {
            await exchange.loadMarkets();
          }
          console.log(`[usePolledOrderbook] ${name} markets preloaded`);
        } catch (err) {
          console.warn(`[usePolledOrderbook] Failed to preload ${name}:`, err);
        }
      })
    );

    console.log('[usePolledOrderbook] All exchanges preloaded');
  } catch (err) {
    console.error('[usePolledOrderbook] Preload failed:', err);
  }
}

/**
 * Get or create a cached exchange instance
 */
async function getOrCreateExchange(
  ccxtModule: CcxtModule,
  exchangeClass: string,
  isFutures: boolean
): Promise<CcxtExchangeType> {
  const cacheKey = `${exchangeClass}_${isFutures}`;

  if (exchangeCache.has(cacheKey)) {
    return exchangeCache.get(cacheKey)!;
  }

  // Type assertion for exchange constructor
  type ExchangeConstructor = new (params?: Record<string, unknown>) => CcxtExchangeType;
  const ExchangeClass = (ccxtModule as unknown as Record<string, ExchangeConstructor>)[exchangeClass];

  if (!ExchangeClass) {
    throw new Error(`Exchange ${exchangeClass} not found in CCXT`);
  }

  // Initialize exchange with rate limiting
  const options: Record<string, unknown> = {
    enableRateLimit: true,
  };

  // For futures, set defaultType
  if (isFutures) {
    options.options = { defaultType: 'future' };
  }

  const exchange = new ExchangeClass(options);
  exchangeCache.set(cacheKey, exchange);

  console.log(`[usePolledOrderbook] Created and cached ${exchangeClass} instance`);
  return exchange;
}

export interface PolledOrderbookState {
  bids: OrderbookPriceLevel[];
  asks: OrderbookPriceLevel[];
  loading: boolean;
  error: string | null;
  lastUpdate: number;
}

interface UsePolledOrderbookOptions {
  exchange: string; // e.g., "binance", "upbit", "bithumb", "binanceusdm"
  symbol: string; // CCXT symbol format e.g., "BTC/USDT", "BTC/KRW"
  enabled?: boolean;
  isFutures?: boolean; // For futures, use binanceusdm
  backendFuturesFallback?: {
    exchange: 'binance';
    canonicalSymbol: string;
    requiredSide: 'bids';
  };
}

type ActiveOrderbookSource = 'ccxt' | 'backend-fallback';
type PrimaryFailureCategory = 'exception' | 'empty' | 'invalid' | 'symbol-mismatch';

type PrimaryBookAssessment =
  | {
      usable: true;
      bids: OrderbookPriceLevel[];
      asks: OrderbookPriceLevel[];
      returnedSymbol?: string;
    }
  | {
      usable: false;
      category: Exclude<PrimaryFailureCategory, 'exception'>;
      message: string;
      returnedSymbol?: string;
    };

const initialState: PolledOrderbookState = {
  bids: [],
  asks: [],
  loading: false,
  error: null,
  lastUpdate: 0,
};

const CANONICAL_FUTURES_SYMBOL_PATTERN = /^[A-Z0-9]+USDT$/;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

function stripSettlementSuffix(symbol: string): string {
  const settlementIndex = symbol.indexOf(':');
  return settlementIndex === -1 ? symbol : symbol.slice(0, settlementIndex);
}

function assessPrimaryBook(
  orderbook: Awaited<ReturnType<CcxtExchangeType['fetchOrderBook']>>,
  requestedSymbol: string,
): PrimaryBookAssessment {
  const returnedSymbol = orderbook.symbol || undefined;

  if (
    returnedSymbol &&
    stripSettlementSuffix(returnedSymbol).toUpperCase() !==
      stripSettlementSuffix(requestedSymbol).toUpperCase()
  ) {
    return {
      usable: false,
      category: 'symbol-mismatch',
      message: `CCXT returned ${returnedSymbol} for ${requestedSymbol}`,
      returnedSymbol,
    };
  }

  const bids: OrderbookPriceLevel[] = (orderbook.bids || []).map((level) => ({
    price: Number(level[0]),
    volume: Number(level[1]),
  }));
  const asks: OrderbookPriceLevel[] = (orderbook.asks || []).map((level) => ({
    price: Number(level[0]),
    volume: Number(level[1]),
  }));

  if (bids.length === 0) {
    return {
      usable: false,
      category: 'empty',
      message: `CCXT returned no bids for ${requestedSymbol}`,
      returnedSymbol,
    };
  }

  if (!Number.isFinite(bids[0].price) || bids[0].price <= 0) {
    return {
      usable: false,
      category: 'invalid',
      message: `CCXT returned an invalid best bid for ${requestedSymbol}`,
      returnedSymbol,
    };
  }

  return { usable: true, bids, asks, returnedSymbol };
}

function validateFallbackConfiguration(
  canonicalSymbol: string,
  requestedSymbol: string,
): string | null {
  if (!CANONICAL_FUTURES_SYMBOL_PATTERN.test(canonicalSymbol)) {
    return `Invalid canonical futures fallback symbol: ${canonicalSymbol}`;
  }

  const requestedParts = stripSettlementSuffix(requestedSymbol)
    .toUpperCase()
    .split('/');
  if (
    requestedParts.length !== 2 ||
    requestedParts[0].length === 0 ||
    requestedParts[1] !== 'USDT' ||
    `${requestedParts[0]}USDT` !== canonicalSymbol
  ) {
    return `Futures fallback symbol ${canonicalSymbol} does not match ${requestedSymbol}`;
  }

  return null;
}

function backendBookFailure(
  bids: OrderbookPriceLevel[] | undefined,
  canonicalSymbol: string,
): string | null {
  if (!bids || bids.length === 0) {
    return `Backend returned no bids for ${canonicalSymbol}`;
  }

  if (!Number.isFinite(bids[0].price) || bids[0].price <= 0) {
    return `Backend returned an invalid best bid for ${canonicalSymbol}`;
  }

  return null;
}

/**
 * Get CCXT exchange class name
 */
function getCcxtExchangeClass(exchange: string, isFutures: boolean): string {
  if (isFutures) {
    return 'binanceusdm'; // Binance USDⓈ-M Futures
  }
  // Map exchange names
  switch (exchange.toLowerCase()) {
    case 'upbit':
      return 'upbit';
    case 'bithumb':
      return 'bithumb';
    case 'binance':
      return 'binance';
    default:
      return exchange.toLowerCase();
  }
}

/**
 * Hook for polling orderbook via CCXT REST API
 *
 * @param options.exchange - Exchange name
 * @param options.symbol - CCXT symbol format (e.g., "BTC/USDT")
 * @param options.enabled - Enable polling (default: true)
 * @param options.isFutures - Use futures exchange (default: false)
 */
export function usePolledOrderbook({
  exchange,
  symbol,
  enabled = true,
  isFutures = false,
  backendFuturesFallback,
}: UsePolledOrderbookOptions): PolledOrderbookState {
  const [state, setState] = useState<PolledOrderbookState>(initialState);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const generationRef = useRef(0);
  const fallbackExchange = backendFuturesFallback?.exchange;
  const fallbackCanonicalSymbol = backendFuturesFallback?.canonicalSymbol;
  const fallbackRequiredSide = backendFuturesFallback?.requiredSide;

  // Initialize CCXT exchange and start polling
  useEffect(() => {
    const generation = ++generationRef.current;
    let active = true;
    const isCurrent = () =>
      active && generationRef.current === generation;

    console.log(`[usePolledOrderbook] Effect: enabled=${enabled}, exchange=${exchange}, symbol=${symbol}`);

    if (!enabled || !exchange || !symbol) {
      // Cleanup
      console.log(`[usePolledOrderbook] Disabled or missing params, cleaning up`);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setState(initialState);
      return () => {
        active = false;
        if (generationRef.current === generation) {
          generationRef.current += 1;
        }
      };
    }

    const fallbackConfigured =
      isFutures &&
      fallbackExchange !== undefined &&
      fallbackCanonicalSymbol !== undefined &&
      fallbackRequiredSide !== undefined;

    if (fallbackConfigured) {
      const configurationError = validateFallbackConfiguration(
        fallbackCanonicalSymbol,
        symbol,
      );
      if (configurationError) {
        console.error('[usePolledOrderbook] Invalid futures fallback configuration', {
          requestedSymbol: symbol,
          fallbackSymbol: fallbackCanonicalSymbol,
          error: configurationError,
        });
        setState({ ...initialState, error: configurationError });
        return () => {
          active = false;
          if (generationRef.current === generation) {
            generationRef.current += 1;
          }
        };
      }
    }

    setState({ ...initialState, loading: true });

    let ccxtExchange: CcxtExchangeType | null = null;
    let activeSource: ActiveOrderbookSource = 'ccxt';
    let ccxtChainInFlight = false;

    const publishBackendBook = async () => {
      const orderbook = await fetchFuturesOrderbook(
        fallbackExchange!,
        fallbackCanonicalSymbol!,
      );
      const failure = backendBookFailure(
        orderbook.bids,
        fallbackCanonicalSymbol!,
      );
      if (failure) {
        throw new Error(failure);
      }

      if (!isCurrent()) return;

      setState({
        bids: orderbook.bids,
        asks: orderbook.asks || [],
        loading: false,
        error: null,
        lastUpdate: Date.now(),
      });
    };

    const fetchLatchedBackend = async () => {
      try {
        await publishBackendBook();
      } catch (error) {
        if (!isCurrent()) return;

        const fallbackError = errorMessage(error);
        console.error('[usePolledOrderbook] Backend futures fallback failed', {
          requestedSymbol: symbol,
          fallbackSymbol: fallbackCanonicalSymbol,
          source: activeSource,
          error: fallbackError,
        });
        setState({
          bids: [],
          asks: [],
          loading: false,
          error: `Backend futures fallback failed for ${fallbackCanonicalSymbol}: ${fallbackError}`,
          lastUpdate: 0,
        });
      }
    };

    const fetchFallbackAfterPrimaryFailure = async (
      category: PrimaryFailureCategory,
      primaryError: string,
      returnedSymbol?: string,
    ) => {
      try {
        await publishBackendBook();
        if (!isCurrent()) return;

        activeSource = 'backend-fallback';
        console.warn('[usePolledOrderbook] Futures orderbook source transition', {
          requestedSymbol: symbol,
          returnedSymbol: returnedSymbol ?? null,
          primaryFailure: category,
          fallbackSymbol: fallbackCanonicalSymbol,
          source: activeSource,
        });
      } catch (error) {
        if (!isCurrent()) return;

        const fallbackError = errorMessage(error);
        const combinedError = `CCXT ${category} for ${symbol}: ${primaryError}; backend fallback ${fallbackCanonicalSymbol} failed: ${fallbackError}`;
        console.error('[usePolledOrderbook] Futures orderbook unavailable', {
          requestedSymbol: symbol,
          returnedSymbol: returnedSymbol ?? null,
          primaryFailure: category,
          fallbackSymbol: fallbackCanonicalSymbol,
          primaryError,
          fallbackError,
        });
        setState({
          bids: [],
          asks: [],
          loading: false,
          error: combinedError,
          lastUpdate: 0,
        });
      }
    };

    const fetchCcxtOrderbook = async () => {
      if (ccxtChainInFlight || !isCurrent()) return;
      ccxtChainInFlight = true;

      try {
        if (activeSource === 'backend-fallback') {
          await fetchLatchedBackend();
          return;
        }

        let orderbook: Awaited<ReturnType<CcxtExchangeType['fetchOrderBook']>>;
        try {
          if (!ccxtExchange) {
            const ccxtModule = await loadCcxtModule();
            if (!isCurrent()) return;

            const exchangeClass = getCcxtExchangeClass(exchange, isFutures);
            ccxtExchange = await getOrCreateExchange(
              ccxtModule,
              exchangeClass,
              isFutures,
            );
            if (!isCurrent()) return;

            console.log(`[usePolledOrderbook] Ready to poll ${exchangeClass} for ${symbol}`);
          }

          orderbook = await ccxtExchange.fetchOrderBook(symbol, 50);
        } catch (error) {
          if (!isCurrent()) return;

          const primaryError = errorMessage(error);
          if (fallbackConfigured) {
            await fetchFallbackAfterPrimaryFailure(
              'exception',
              primaryError,
            );
          } else {
            console.error(`[usePolledOrderbook] ${exchange}/${symbol} fetch error:`, primaryError);
            setState((prev) => ({
              ...prev,
              loading: false,
              error: primaryError,
            }));
          }
          return;
        }

        if (!isCurrent()) return;

        if (fallbackConfigured) {
          const assessment = assessPrimaryBook(orderbook, symbol);
          if (!assessment.usable) {
            await fetchFallbackAfterPrimaryFailure(
              assessment.category,
              assessment.message,
              assessment.returnedSymbol,
            );
            return;
          }

          setState({
            bids: assessment.bids,
            asks: assessment.asks,
            loading: false,
            error: null,
            lastUpdate: Date.now(),
          });
          return;
        }

        const bids: OrderbookPriceLevel[] = (orderbook.bids || []).map((level) => ({
          price: Number(level[0]),
          volume: Number(level[1]),
        }));
        const asks: OrderbookPriceLevel[] = (orderbook.asks || []).map((level) => ({
          price: Number(level[0]),
          volume: Number(level[1]),
        }));

        setState({
          bids,
          asks,
          loading: false,
          error: null,
          lastUpdate: Date.now(),
        });
      } finally {
        ccxtChainInFlight = false;
      }
    };

    // Preserve the existing CCXT-only path for callers that did not opt in.
    const fetchStandardCcxtOrderbook = async () => {
      if (!ccxtExchange || !isCurrent()) return;

      try {
        const orderbook = await ccxtExchange.fetchOrderBook(symbol, 50);
        if (!isCurrent()) return;

        const bids: OrderbookPriceLevel[] = (orderbook.bids || []).map((level) => ({
          price: Number(level[0]),
          volume: Number(level[1]),
        }));
        const asks: OrderbookPriceLevel[] = (orderbook.asks || []).map((level) => ({
          price: Number(level[0]),
          volume: Number(level[1]),
        }));

        setState({
          bids,
          asks,
          loading: false,
          error: null,
          lastUpdate: Date.now(),
        });
      } catch (error) {
        if (!isCurrent()) return;

        const primaryError = errorMessage(error);
        console.error(`[usePolledOrderbook] ${exchange}/${symbol} fetch error:`, primaryError);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: primaryError,
        }));
      }
    };

    // Fetch function for Upbit (uses server API)
    const fetchUpbitFromServer = async () => {
      if (!isCurrent()) return;

      try {
        const { bids, asks } = await fetchUpbitOrderbook(symbol);

        if (!isCurrent()) return;

        setState({
          bids,
          asks,
          loading: false,
          error: null,
          lastUpdate: Date.now(),
        });
      } catch (err) {
        if (!isCurrent()) return;

        const message = errorMessage(err);
        console.error(`[usePolledOrderbook] Upbit server fetch error:`, message);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: message,
        }));
      }
    };

    // Fetch function for Bithumb (uses direct API with server fallback)
    // Note: CCXT's Bithumb market list is outdated and missing newer tokens like ALT
    const fetchBithumbFromServer = async () => {
      if (!isCurrent()) return;

      try {
        const { bids, asks } = await fetchBithumbOrderbook(symbol);

        if (!isCurrent()) return;

        setState({
          bids,
          asks,
          loading: false,
          error: null,
          lastUpdate: Date.now(),
        });
      } catch (err) {
        if (!isCurrent()) return;

        const message = errorMessage(err);
        console.error(`[usePolledOrderbook] Bithumb fetch error:`, message);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: message,
        }));
      }
    };

    // Load CCXT and initialize exchange (uses cache for fast init)
    const initAndPoll = async () => {
      try {
        // Use direct API for Upbit to bypass CCXT's loadMarkets() 429 issue
        if (exchange.toLowerCase() === 'upbit') {
          console.log(`[usePolledOrderbook] Using direct API for Upbit ${symbol}`);

          // Fetch immediately
          await fetchUpbitFromServer();

          if (!isCurrent()) return;

          // Start polling interval
          intervalRef.current = setInterval(() => {
            if (!isCurrent()) return;
            void fetchUpbitFromServer();
          }, POLL_INTERVAL_MS);
          return;
        }

        // Use direct API for Bithumb - CCXT market list is outdated (missing ALT, etc.)
        if (exchange.toLowerCase() === 'bithumb') {
          console.log(`[usePolledOrderbook] Using direct API for Bithumb ${symbol}`);

          // Fetch immediately
          await fetchBithumbFromServer();

          if (!isCurrent()) return;

          // Start polling interval
          intervalRef.current = setInterval(() => {
            if (!isCurrent()) return;
            void fetchBithumbFromServer();
          }, POLL_INTERVAL_MS);
          return;
        }

        // Use server API for Coinone - public API has CORS restrictions
        if (exchange.toLowerCase() === 'coinone') {
          console.log(`[usePolledOrderbook] Using server API for Coinone ${symbol}`);

          // Fetch function for Coinone
          const fetchCoinoneFromServer = async () => {
            if (!isCurrent()) return;

            try {
              const { bids, asks } = await fetchCoinoneOrderbook(symbol);

              if (!isCurrent()) return;

              setState({
                bids,
                asks,
                loading: false,
                error: null,
                lastUpdate: Date.now(),
              });
            } catch (err) {
              if (!isCurrent()) return;

              const message = errorMessage(err);
              console.error(`[usePolledOrderbook] Coinone fetch error:`, message);
              setState((prev) => ({
                ...prev,
                loading: false,
                error: message,
              }));
            }
          };

          // Fetch immediately
          await fetchCoinoneFromServer();

          if (!isCurrent()) return;

          // Start polling interval
          intervalRef.current = setInterval(() => {
            if (!isCurrent()) return;
            void fetchCoinoneFromServer();
          }, POLL_INTERVAL_MS);
          return;
        }

        if (fallbackConfigured) {
          // Start immediately so interval ticks can prove the entire
          // CCXT-to-backend chain remains serialized while it is in flight.
          void fetchCcxtOrderbook();
          intervalRef.current = setInterval(() => {
            if (!isCurrent()) return;
            void fetchCcxtOrderbook();
          }, POLL_INTERVAL_MS);
          return;
        }

        // Preserve the existing CCXT-only initialization and polling order.
        const ccxtModule = await loadCcxtModule();
        if (!isCurrent()) return;

        const exchangeClass = getCcxtExchangeClass(exchange, isFutures);
        ccxtExchange = await getOrCreateExchange(
          ccxtModule,
          exchangeClass,
          isFutures,
        );
        if (!isCurrent()) return;

        console.log(`[usePolledOrderbook] Ready to poll ${exchangeClass} for ${symbol}`);
        await fetchStandardCcxtOrderbook();
        if (!isCurrent()) return;

        intervalRef.current = setInterval(() => {
          if (!isCurrent()) return;
          void fetchStandardCcxtOrderbook();
        }, POLL_INTERVAL_MS);
      } catch (err) {
        if (!isCurrent()) return;

        const message = errorMessage(err);
        console.error(`[usePolledOrderbook] Init error:`, message);
        setState((prev) => ({
          ...prev,
          loading: false,
          error: message,
        }));
      }
    };

    initAndPoll();

    // Cleanup on unmount or when deps change
    return () => {
      console.log(`[usePolledOrderbook] Cleanup: ${exchange}/${symbol}`);
      active = false;
      if (generationRef.current === generation) {
        generationRef.current += 1;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    enabled,
    exchange,
    symbol,
    isFutures,
    fallbackExchange,
    fallbackCanonicalSymbol,
    fallbackRequiredSide,
  ]);

  return state;
}
